import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import '@/styles/matching.css';
import '@/styles/offers.css';
import { ccySym } from '@/utils/currencyUtils';
import { cityFlag as flag } from '@/utils/cityFlag';
import ConfirmDialog from '@/components/ConfirmDialog';
import { isEntreprise } from '@/utils/entreprise';
import { BACKEND_URL } from '@/config';
import VerificationBadges from '@/components/VerificationBadges';
import {
  ArrowLeft, ArrowRight, Archive, Briefcase, Check, CircleCheck, Clock, Eye, Inbox, MailPlus, MessageCircle, Package,
  Quote, Route, Sparkles, Star, Trophy, User, X,
} from 'lucide-react';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

function fmtDate(iso) {
  if (!iso) return '·';
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtAgo(iso) {
  if (!iso) return '';
  const d = Date.now() - new Date(iso).getTime();
  const m = Math.floor(d / 60000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  return `${days}j`;
}

const STATUS_LABEL = {
  PENDING:   { label: 'En attente',   tone: 'pending' },
  ACCEPTED:  { label: 'Acceptée',     tone: 'accepted' },
  REJECTED:  { label: 'Refusée',      tone: 'rejected' },
  WITHDRAWN: { label: 'Retirée',      tone: 'muted' },
  EXPIRED:   { label: 'Expirée',      tone: 'muted' },
};

function ScoreBreakdown({ match }) {
  if (!match) return null;
  const items = [
    { key: 'geo',   label: 'Géo',        val: match.geo,   tone: 'gold' },
    { key: 'price', label: 'Prix',       val: match.price, tone: 'teal' },
    { key: 'time',  label: 'Temps',      val: match.time,  tone: 'navy' },
    { key: 'rep',   label: 'Réputation', val: match.rep,   tone: 'gold' },
  ];
  return (
    <div className="of-score-breakdown">
      {items.map(i => (
        <div key={i.key} className="of-score-cell">
          <div className={`of-score-val of-tone-${i.tone}`}>{i.val}</div>
          <div className="of-score-lbl">{i.label}</div>
        </div>
      ))}
    </div>
  );
}

function ScoreDial({ total }) {
  const pct = Math.min(100, Math.max(0, total || 0));
  const angle = (pct / 100) * 360;
  return (
    <div className="of-dial" style={{ background: `conic-gradient(var(--gold) ${angle}deg, var(--bdl) 0deg)` }}>
      <div className="of-dial-inner">
        <div className="of-dial-val">{pct}</div>
      </div>
    </div>
  );
}

/**
 * Offers page reused by ShipmentOffersPage and TripOffersPage.
 *
 * Props:
 *   kind: 'shipment' | 'trip'
 */
export default function OffersPage({ kind = 'shipment' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [listing, setListing] = useState(null);
  const [offers, setOffers] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pending: 0, accepted: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [tab, setTab] = useState('pending');

  const isShip = kind === 'shipment';
  const basePath = isShip ? 'shipments' : 'trips';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, o] = await Promise.all([
        axios.get(`${API}/${basePath}/${id}`),
        axios.get(`${API}/${basePath}/${id}/offers`),
      ]);
      setListing(l.data);
      setOffers(o.data?.data || []);
      setMeta(o.data?.meta || { total: 0, pending: 0, accepted: 0, rejected: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [basePath, id, toast]);

  useEffect(() => { load(); }, [load]);

  const accept = (p) => setAcceptTarget(p);

  const doAccept = async () => {
    const p = acceptTarget;
    setAcceptTarget(null);
    setBusyId(p.id);
    try {
      const r = await axios.post(`${API}/propositions/${p.id}/accept`);
      toast.success('Proposition acceptée — mission créée');
      if (r.data?.mission_id) {
        setTimeout(() => navigate(`/app/missions/${r.data.mission_id}`), 600);
      } else {
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setBusyId(null); }
  };

  const reject = (p) => { setRejectTarget(p); setRejectReason(''); };

  const doReject = async () => {
    const p = rejectTarget;
    setRejectTarget(null);
    setBusyId(p.id);
    try {
      await axios.post(`${API}/propositions/${p.id}/reject`, { reason: rejectReason });
      toast.info('Proposition refusée');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setBusyId(null); }
  };

  const visibleOffers = offers.filter(o => {
    if (tab === 'pending')  return o.status === 'PENDING';
    if (tab === 'accepted') return o.status === 'ACCEPTED';
    if (tab === 'rejected') return ['REJECTED', 'WITHDRAWN', 'EXPIRED'].includes(o.status);
    return true;
  });

  if (loading) {
    return <div className="offers-page"><div className="of-loading">Chargement des offres…</div></div>;
  }

  const route = listing
    ? <>{flag(listing.departure_city)} {listing.departure_city} → {flag(listing.arrival_city)} {listing.arrival_city}</>
    : '';
  const goMatching = () => navigate(isShip ? `/app/shipments/${id}/matching` : `/app/trips/${id}/matching`);

  return (
    <div className="offers-page" data-testid="offers-page">
      <button className="of-back" onClick={() => navigate(-1)} data-testid="btn-offers-back">
        <ArrowLeft size={16} aria-hidden="true" /> Retour
      </button>

      <div className="of-header">
        <div>
          <h1 className="of-title">
            <MailPlus size={26} aria-hidden="true" /> Offres reçues
          </h1>
          {listing && (
            <div className="of-subtitle">
              {route}
              {isShip && listing.weight_kg ? ` · ${listing.weight_kg}kg · ${listing.price_offered ? `budget ${listing.price_offered} ${ccySym(listing.primary_currency || 'EUR')}` : 'Budget libre'}` : ''}
              {!isShip && listing.available_weight_kg ? ` · ${listing.available_weight_kg}kg dispo · ${listing.price_per_kg} ${ccySym(listing.primary_currency || 'EUR')}/kg` : ''}
            </div>
          )}
        </div>
        <button className="of-cta-matching" onClick={goMatching} data-testid="btn-offers-explore">
          <Sparkles size={16} aria-hidden="true" /> {isShip ? 'Explorer les trajets' : 'Explorer les colis'}
        </button>
      </div>

      <div className="of-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'pending'}
          className={`of-tab${tab === 'pending' ? ' on' : ''}`}
          onClick={() => setTab('pending')}
          data-testid="tab-offers-pending"
        >
          <Clock size={15} aria-hidden="true" /> En attente
          {meta.pending > 0 && <span className="of-tab-badge">{meta.pending}</span>}
        </button>
        <button
          role="tab"
          aria-selected={tab === 'accepted'}
          className={`of-tab${tab === 'accepted' ? ' on' : ''}`}
          onClick={() => setTab('accepted')}
          data-testid="tab-offers-accepted"
        >
          <CircleCheck size={15} aria-hidden="true" /> Acceptées ({meta.accepted})
        </button>
        <button
          role="tab"
          aria-selected={tab === 'rejected'}
          className={`of-tab${tab === 'rejected' ? ' on' : ''}`}
          onClick={() => setTab('rejected')}
          data-testid="tab-offers-rejected"
        >
          <Archive size={15} aria-hidden="true" /> Archivées
        </button>
      </div>

      {visibleOffers.length === 0 ? (
        <div className="of-empty">
          <Inbox size={44} aria-hidden="true" />
          <h3>Aucune offre {tab === 'pending' ? 'en attente' : tab === 'accepted' ? 'acceptée' : 'archivée'}</h3>
          <p>
            {tab === 'pending'
              ? 'Pas encore de proposition reçue sur cette annonce. Utilisez le matching intelligent pour solliciter les meilleurs profils.'
              : 'Aucune offre dans cette catégorie pour le moment.'}
          </p>
          {tab === 'pending' && (
            <button className="of-cta-matching" onClick={goMatching}>
              <Sparkles size={16} aria-hidden="true" /> Explorer le matching
            </button>
          )}
        </div>
      ) : (
        <div className="of-grid">
          {visibleOffers.map((o, idx) => {
            const u = o.from_user || {};
            const m = o.match || {};
            const s = STATUS_LABEL[o.status] || { label: o.status, tone: 'muted' };
            const isBest = tab === 'pending' && idx === 0 && (m.total || 0) >= 70;
            return (
              <div key={o.id} className={`of-card of-status-${s.tone}${isBest ? ' is-best' : ''}`} data-testid={`offer-${o.id}`}>
                {isBest && <div className="of-best-flag"><Trophy size={12} aria-hidden="true" /> Meilleur match</div>}

                <div className="of-card-head">
                  <ScoreDial total={m.total} />
                  <div className="of-from">
                    <div className="of-from-name">
                      {u.first_name} {u.last_name}
                      <VerificationBadges user={u} compact />
                      {isEntreprise(u) && <span className="of-badge of-badge-entreprise"><Briefcase size={11} aria-hidden="true" /> Entreprise</span>}
                    </div>
                    <div className="of-from-meta">
                      {u.avg_rating ? (
                        <span><Star size={12} fill="currentColor" aria-hidden="true" /> {u.avg_rating.toFixed(1)} · {u.rating_count || 0} avis</span>
                      ) : (
                        <span className="of-muted">Nouveau membre</span>
                      )}
                      {u.completed_missions != null && <span> · {u.completed_missions} missions</span>}
                    </div>
                  </div>
                  <span className={`of-status of-status-pill-${s.tone}`}>{s.label}</span>
                </div>

                {/* Trip/shipment of the proposer */}
                {(o.trip || o.shipment) && (
                  <div className="of-listing-line">
                    {o.trip ? <Route size={15} aria-hidden="true" /> : <Package size={15} aria-hidden="true" />}
                    <strong>
                      {(o.trip || o.shipment).departure_city} → {(o.trip || o.shipment).arrival_city}
                    </strong>
                    <span> · {fmtDate((o.trip || o.shipment).departure_date)}</span>
                  </div>
                )}

                {o.message && (
                  <div className="of-message">
                    <Quote size={14} aria-hidden="true" />
                    <span>{o.message}</span>
                  </div>
                )}

                <ScoreBreakdown match={m} />

                <div className="of-foot">
                  <div>
                    <div className="of-price-lbl">Prix proposé</div>
                    <div className="of-price-amt">{Number(o.proposed_price || 0).toFixed(0)} {ccySym(o.currency || 'EUR')}</div>
                  </div>
                  <div className="of-time">il y a {fmtAgo(o.created_at)}</div>
                </div>

                <div className="of-actions">
                  {o.status === 'PENDING' ? (
                    <>
                      <button className="of-btn of-btn-ghost" onClick={() => navigate(`/app/profile/${u.id}`)} data-testid={`btn-profile-${o.id}`}>
                        <User size={13} aria-hidden="true" /> Profil
                      </button>
                      <button className="of-btn of-btn-ghost" onClick={() => navigate('/app/messages')} data-testid={`btn-msg-${o.id}`}>
                        <MessageCircle size={13} aria-hidden="true" /> Message
                      </button>
                      {(o.trip?.id || o.shipment?.id) && (
                        <button className="of-btn of-btn-ghost" onClick={() => navigate(o.trip ? `/app/trips/${o.trip.id}` : `/app/shipments/${o.shipment.id}`)} data-testid={`btn-details-${o.id}`}>
                          <Eye size={13} aria-hidden="true" /> Détails
                        </button>
                      )}
                      <button className="of-btn of-btn-danger" disabled={busyId === o.id} onClick={() => reject(o)} data-testid={`btn-reject-${o.id}`}>
                        <X size={13} aria-hidden="true" /> Refuser
                      </button>
                      <button className="of-btn of-btn-primary" disabled={busyId === o.id} onClick={() => accept(o)} data-testid={`btn-accept-${o.id}`}>
                        <Check size={13} aria-hidden="true" /> Accepter {Number(o.proposed_price || 0).toFixed(0)} {ccySym(o.currency || 'EUR')}
                      </button>
                    </>
                  ) : o.status === 'ACCEPTED' && o.mission_id ? (
                    <button className="of-btn of-btn-primary" onClick={() => navigate(`/app/missions/${o.mission_id}`)} data-testid={`btn-mission-${o.id}`}>
                      <ArrowRight size={13} aria-hidden="true" /> Voir la mission
                    </button>
                  ) : null}
                </div>

                {o.status === 'REJECTED' && o.reject_reason && (
                  <div className="of-reject-reason">
                    <strong>Motif :</strong> {o.reject_reason}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!acceptTarget}
        onClose={() => setAcceptTarget(null)}
        onConfirm={doAccept}
        variant="financial"
        icon="check"
        title="Accepter la proposition"
        subtitle={acceptTarget ? `${acceptTarget.from_user?.first_name || 'Ce membre'} ${acceptTarget.from_user?.last_name || ''}`.trim() : undefined}
        amount={acceptTarget ? `${Number(acceptTarget.proposed_price || 0).toFixed(0)} ${ccySym(acceptTarget.currency || 'EUR')}` : undefined}
        confirmLabel="Confirmer l'acceptation"
        busy={!!busyId}
      />

      <ConfirmDialog
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={doReject}
        variant="destructive"
        icon="ban"
        title="Refuser la proposition ?"
        subtitle={rejectTarget ? `De ${rejectTarget.from_user?.first_name || 'ce membre'} ${rejectTarget.from_user?.last_name || ''}`.trim() : undefined}
        confirmLabel="Confirmer le refus"
        inputPrompt={{ label: 'Motif du refus (optionnel)', placeholder: 'Ex : prix trop élevé, dates incompatibles…' }}
        inputValue={rejectReason}
        onInputChange={setRejectReason}
        busy={!!busyId}
      />
    </div>
  );
}
